import React, { useEffect, useRef } from 'react';

import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { Grid2 as Grid, styled } from '@mui/material';

import { getCssVariable } from '../../helper/get-css-variable';
import ResponsiveGrid from '../../layout/grid';
import { TimelineItem, TimelineItemProps } from './timeline-item';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

let topGridStart = -1;
let bottomGridStart = -1;

export type TimelineProps = {
  items: TimelineItemProps[];
};

export const Timeline = ({ ...props }: TimelineProps) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineContentRef = useRef<HTMLDivElement>(null);
  const scrollTriggerRef = useRef<gsap.plugins.ScrollTrigger | null>(null);

  const navigateToItem = (targetIndex: number) => {
    if (
      !timelineContentRef.current ||
      !scrollTriggerRef.current ||
      !timelineRef.current
    ) {
      return;
    }

    const content = timelineContentRef.current;
    const wrapper = timelineRef.current;

    const timelineItems = content.querySelectorAll('[data-timeline-item]');
    const targetItem = timelineItems[targetIndex];

    if (!targetItem) {
      return;
    }

    const targetPosition = getTargetPosition(targetItem, content);
    const maxScrollPosition = getMaxScrollPosition(content, wrapper);
    const scrollPosition = Math.min(
      Math.abs(targetPosition),
      maxScrollPosition,
    );

    const scrollTrigger = scrollTriggerRef.current;
    const scrollStart = scrollTrigger.start;
    const scrollEnd = scrollTrigger.end;
    const scrollRange = scrollEnd - scrollStart;
    const progress = scrollPosition / maxScrollPosition;

    const targetScrollY = scrollStart + progress * scrollRange;

    gsap.to(window, {
      scrollTo: targetScrollY,
      duration: 1,
      ease: 'power2.out',
    });
  };

  const getTargetPosition = (targetItem: Element, content: HTMLDivElement) => {
    const itemRect = targetItem.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    return itemRect.left - contentRect.left;
  };

  const getMaxScrollPosition = (
    content: HTMLDivElement,
    wrapper: HTMLDivElement,
  ) => {
    const contentWidth = content.scrollWidth;
    const viewportWidth = wrapper.offsetWidth;
    return contentWidth - viewportWidth;
  };

  useEffect(() => {
    if (!timelineRef.current || !timelineContentRef.current) {
      return;
    }

    const content = timelineContentRef.current;
    const wrapper = timelineRef.current;

    // calculate horizontal scroll
    const contentWidth = content.scrollWidth;
    const viewportWidth = wrapper.offsetWidth; // width of parent element
    const scrollDistance = contentWidth - viewportWidth;

    const headerHeight = getCssVariable('--header-height');

    // GSAP timeline for horizontal scroll
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: timelineRef.current,
        start: `top top+=${headerHeight}`,
        end: `+=${scrollDistance}`,
        pin: true,
        scrub: 1,
        invalidateOnRefresh: true,
      },
    });

    scrollTriggerRef.current = tl.scrollTrigger;

    tl.fromTo(content, { x: 0 }, { x: -scrollDistance, ease: 'none' });

    return () => {
      scrollTriggerRef.current = null;
      tl.kill();
    };
  }, []);

  return (
    <div ref={timelineRef}>
      <ResponsiveGrid>
        <Grid size={10} offset={1}>
          <NavigationWrapper>
            {props.items.map((item: TimelineItemProps, index: number) => {
              if (item.yearTo) {
                return (
                  <NavigationItem
                    key={index}
                    onClick={() => navigateToItem(index)}
                  >
                    <div>{item.year}</div>
                    <div>{item.yearTo}</div>
                  </NavigationItem>
                );
              } else {
                return null;
              }
            })}
          </NavigationWrapper>
        </Grid>
      </ResponsiveGrid>
      <TimelineWrapper>
        <TimelineContent ref={timelineContentRef}>
          {props.items.map((item, index) => {
            if (item.align === 'top') {
              topGridStart += 2;
              bottomGridStart = topGridStart - 1;
            } else if (item.align === 'bottom') {
              bottomGridStart += 2;
              topGridStart = bottomGridStart - 1;
            }

            const line = item.align === 'top' ? 1 : 3;
            const itemStart =
              item.align === 'top' ? topGridStart : bottomGridStart;

            return (
              <React.Fragment key={index}>
                <div
                  data-timeline-item
                  style={{
                    gridArea: `${line} / ${itemStart} / ${line + 1} / ${itemStart + 2}`,
                    margin:
                      item.align === 'bottom' ? '-27.5px 0 0' : '0 0 -27.5px',
                    display: 'flex',
                    alignItems:
                      item.align === 'bottom' ? 'flex-start' : 'flex-end',
                  }}
                >
                  <TimelineItem {...item} />
                </div>
                <TimelineLine
                  index={index}
                  style={{
                    gridArea: `2 / ${itemStart} / 3 / ${itemStart + 2}`,
                  }}
                />
              </React.Fragment>
            );
          })}
        </TimelineContent>
      </TimelineWrapper>
    </div>
  );
};

const NavigationItem = styled('div')(({ theme }) => ({
  ...theme.typography.h1,
  width: '177px',
  cursor: 'pointer',
  transition: 'color 0.2s ease',

  '&:hover': {
    color: 'var(--color-light-red)',

    'div:first-of-type': {
      fontSize: '70px',
      fontWeight: theme.typography.fontWeightMedium,
    },
  },

  'div:first-of-type': {
    height: 'calc(var(--line-height-h1) * 70px)',
    display: 'flex',
    alignItems: 'flex-end',
  },

  'div:last-of-type': {
    ...theme.typography.footnote,
    marginTop: 'var(--spacer-xxs)',
  },
}));

const TimelineWrapper = styled('div')(() => ({
  overflow: 'hidden',
  marginTop: 'var(--spacer-xxxl)',
}));

const NavigationWrapper = styled('div')(() => ({
  display: 'flex',
  columnGap: '30px',
}));

const TimelineContent = styled('div')(() => ({
  display: 'grid',
  gridAutoColumns: '322px',
  gridAutoFlow: 'column',
  width: 'max-content',
  willChange: 'transform',
  padding:
    '0 calc(var(--margin) + var(--gutter) + ((100vw - (var(--margin) * 2) - (var(--gutter) * 11))) / 12)',
}));

const TimelineLine = styled('div')<{
  index: number;
}>(({ index }) => ({
  height: '5px',
  backgroundColor: 'var(--color-black)',
}));
